import { AxiosRequestConfig } from 'axios';

import axiosServices from 'utils/axios';
import {
  User,
  UserForm,
  UserSmtpIntegrationItem,
  UserSmtpIntegration,
  UserSmtpIntegrationForm,
  UserOnboardingForm,
  UserSyncIntegration
} from 'types/user';
import {
  EmailVerificationType,
  Notification,
  UserRelationsInfo,
  UserSettingsForm,
  UserSettingsList,
  ENotificationsType,
  ENotificationsValue
} from 'types/user-settings';
import { PageMetaData } from 'types/pagination';
import { SetupChecklist } from 'types/user-profile';

export async function getUsersList(queryParams: Record<string, any> = {}, headers: Record<string, any> = {}): Promise<User[]> {
  try {
    const config: AxiosRequestConfig = {};
    if (Object.keys(queryParams).length > 0) {
      const params = new URLSearchParams(queryParams);
      config.params = params;
    }
    if (Object.keys(headers).length > 0) {
      config.headers = headers;
    }

    const response = await axiosServices.get('/api/user', config);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error getting users:', error);
    throw error;
  }
}

interface UserSummarized {
  id: string;
  fullName: string;
}

export async function getUsersSummarized(): Promise<UserSummarized[]> {
  try {
    const response = await axiosServices.get<UserSummarized[]>('/api/user/summarized');
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error getting users:', error);
    throw error;
  }
}

export async function updateUserPassword(token: string, password: string) {
  try {
    const response = await axiosServices.post(`api/user/reset-password/${token}`, { password });
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error when updating the user password:', error);
    throw error;
  }
}

export async function resetPasswordLink(email: string) {
  try {
    const response = await axiosServices.post(`api/user/forgot-password`, { email });
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error sending the reset password link:', error);
    throw error;
  }
}

export async function getUserById(userId: string, headers: Record<string, any> = {}): Promise<UserSettingsList> {
  try {
    const config: AxiosRequestConfig = {};
    if (Object.keys(headers).length > 0) {
      config.headers = headers;
    }

    const response = await axiosServices.get(`/api/user/${userId}`, config);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error getting user:', error);
    throw error;
  }
}

export async function getUserRelationsInfo(userId: string, headers: Record<string, any> = {}): Promise<UserRelationsInfo> {
  try {
    const config: AxiosRequestConfig = {};
    if (Object.keys(headers).length > 0) {
      config.headers = headers;
    }

    const response = await axiosServices.get(`/api/user/relations/${userId}`, config);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error getting user relations info:', error);
    throw error;
  }
}

export async function updateUserById(userId: string, user: Partial<UserSettingsForm>, headers: Record<string, any> = {}): Promise<void> {
  try {
    const config: AxiosRequestConfig = {};
    if (Object.keys(headers).length > 0) {
      config.headers = headers;
    }

    // Normalize notifications: if empty array, send DISABLED
    let normalizedUser: Partial<UserSettingsForm> = user;
    if (Array.isArray(user.notifications)) {
      const notifications = user.notifications;
      normalizedUser = {
        ...user,
        notifications:
          notifications.length === 0 ? [{ type: ENotificationsType.MESSAGES_REPLIES, value: ENotificationsValue.DISABLED }] : notifications
      };
    }

    const response = await axiosServices.patch(`/api/user/${userId}`, normalizedUser, config);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error updating user:', error);
    throw error;
  }
}

// Persist the user's UI theme choice so it follows them across browsers/devices.
// Best-effort: the theme is already applied locally + stored in localStorage, so a
// failed request must never disrupt the toggle — swallow and log.
export async function updateUserThemePreference(userId: string, themePreference: 'light' | 'dark'): Promise<void> {
  try {
    await axiosServices.patch(`/api/user/${userId}`, { themePreference });
  } catch (error) {
    console.error('There was an error persisting theme preference:', error);
  }
}

export async function verifyUserPhone(userId: string, headers: Record<string, any> = {}, phone: string = ''): Promise<void> {
  try {
    const config: AxiosRequestConfig = {};
    if (Object.keys(headers).length > 0) {
      config.headers = headers;
    }

    const response = await axiosServices.post(`/api/twilio/verify-phone/${userId}`, { phone }, config);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error verifying phone:', error);
    throw error;
  }
}

export async function verifyUserEmail(
  userId: string,
  type: EmailVerificationType,
  headers: Record<string, any> = {},
  email: string = ''
): Promise<void> {
  try {
    const config: AxiosRequestConfig = {};
    if (Object.keys(headers).length > 0) {
      config.headers = headers;
    }

    const response = await axiosServices.post(`/api/user/verify-email/${userId}`, { type, email }, config);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error verifying email:', error);
    throw error;
  }
}

export interface UploadImageResponse {
  ETag: string;
  ServerSideEncryption: string;
  Location: string;
  key: string;
  Key: string;
  Bucket: string;
}

export async function uploadProfileImage(userId: string, file: File, headers: Record<string, any> = {}): Promise<UploadImageResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...headers
      }
    };

    const response = await axiosServices.post(`/api/user/${userId}/profile-image`, formData, config);
    const data = response.data.data;
    return data;
  } catch (error) {
    console.error('There was an error upload profile image:', error);
    throw error;
  }
}

export async function removeUserProfileUrl(userId: string): Promise<void> {
  try {
    const response = await axiosServices.delete(`/api/user/${userId}/profile-image`);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error deleting user profile url:', error);
    throw error;
  }
}

export async function deleteUserAccount(userId: string, subUserId: string): Promise<{ message: string }> {
  try {
    const response = await axiosServices.delete(`/api/user/${userId}`, { data: { subUserId } });
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error deleting user:', error);
    throw error;
  }
}

export async function createUser(user: UserForm): Promise<User> {
  try {
    const response = await axiosServices.post('/api/user', user);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error creating user:', error);
    throw error;
  }
}

export async function createUserOnboarding(user: UserOnboardingForm): Promise<User> {
  try {
    const response = await axiosServices.post('/api/user/onboarding', user);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error creating user:', error);
    throw error;
  }
}

export async function getNotifications(queryParams: Record<string, any> = {}, headers: Record<string, any> = {}): Promise<Notification[]> {
  try {
    const config: AxiosRequestConfig = {};
    if (Object.keys(queryParams).length > 0) {
      const params = new URLSearchParams(queryParams);
      config.params = params;
    }
    if (Object.keys(headers).length > 0) {
      config.headers = headers;
    }

    const response = await axiosServices.get('/api/notification', config);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error getting notifications:', error);
    throw error;
  }
}

export async function getUnreadNotificationsCount(headers: Record<string, any> = {}): Promise<number> {
  try {
    const config: AxiosRequestConfig = {};
    if (Object.keys(headers).length > 0) {
      config.headers = headers;
    }

    const response = await axiosServices.get('/api/notification/unread', config);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error getting unread notifications:', error);
    throw error;
  }
}

export async function updateNotificationById(notificationId: string, headers: Record<string, any> = {}): Promise<void> {
  try {
    const config: AxiosRequestConfig = {};
    if (Object.keys(headers).length > 0) {
      config.headers = headers;
    }

    const response = await axiosServices.patch(`/api/notification/${notificationId}`, config);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error updating notification:', error);
    throw error;
  }
}

export async function updateAllNotifications(headers: Record<string, any> = {}): Promise<void> {
  try {
    const config: AxiosRequestConfig = {};
    if (Object.keys(headers).length > 0) {
      config.headers = headers;
    }

    const response = await axiosServices.post('/api/notification', config);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error updating notification:', error);
    throw error;
  }
}

/**
 * Linking a calendar or meeting account needs a signed, single-use `state` from the backend: the
 * bare user id used to be enough, which let anyone link their own account to any user. "Login
 * with Google/Microsoft" (`'login'`) keeps its plain URL.
 */
async function oauthStartUrl(provider: 'google' | 'microsoft' | 'zoom', userId: string): Promise<string> {
  const base = `${process.env.REACT_APP_API_URL}api/auth/${provider}`;
  if (userId === 'login') return `${base}?userId=login`;
  const { data } = await axiosServices.post('api/auth/oauth-link-state', { provider, userId });
  return `${base}?state=${encodeURIComponent(data.state)}`;
}

export async function handleGoogleAuth(userId: string): Promise<void> {
  // Construct the URL for Google OAuth, including the userId in the query parameters.
  const googleAuthUrl = await oauthStartUrl('google', userId);

  // Redirect to this URL for the OAuth flow.
  window.location.href = googleAuthUrl;

  // // Open the OAuth URL in a new window with specific dimensions and features.
  // window.open(googleAuthUrl, '_blank', 'width=500,height=600,noopener,noreferrer');
}

export async function unsyncFromGoogle(userId: string): Promise<void> {
  try {
    const response = await axiosServices.patch(`/api/calendar/unsync-google/${userId}`);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error unsync from Google:', error);
    throw error;
  }
}

export async function handleZoomAuth(userId: string): Promise<void> {
  // Construct the URL for Google OAuth, including the userId in the query parameters.
  const googleAuthUrl = await oauthStartUrl('zoom', userId);
  // Redirect to this URL for the OAuth flow.
  window.location.href = googleAuthUrl;

  // // Open the OAuth URL in a new window with specific dimensions and features.
  // window.open(googleAuthUrl, '_blank', 'width=500,height=600,noopener,noreferrer');
}

export async function unsyncFromZoom(userId: string): Promise<void> {
  try {
    const response = await axiosServices.patch(`/api/calendar/unsync-zoom/${userId}`);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error unsync from Zoom:', error);
    throw error;
  }
}

export async function handleMicrosoftAuth(userId: string): Promise<void> {
  // Construct the URL for Google OAuth, including the userId in the query parameters.
  const microsoftAuthUrl = await oauthStartUrl('microsoft', userId);
  // Redirect to this URL for the OAuth flow.
  window.location.href = microsoftAuthUrl;

  // // Open the OAuth URL in a new window with specific dimensions and features.
  // window.open(googleAuthUrl, '_blank', 'width=500,height=600,noopener,noreferrer');
}

export async function unsyncFromMicrosoft(userId: string): Promise<void> {
  try {
    const response = await axiosServices.patch(`/api/calendar/unsync-microsoft/${userId}`);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error unsync from Microsoft:', error);
    throw error;
  }
}

export async function getUserSmtpIntegrationsList(userId: string): Promise<UserSmtpIntegrationItem[]> {
  try {
    const response = await axiosServices.get<UserSmtpIntegrationItem[]>(`/api/user-smtp-integration/${userId}`);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error while getting user smtp integration', error);
    throw error;
  }
}

export async function getUserSmtpIntegrationById(id: string): Promise<UserSmtpIntegration> {
  try {
    const response = await axiosServices.get<UserSmtpIntegration>(`/api/user-smtp-integration/${id}`);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error while getting user smtp integration', error);
    throw error;
  }
}

export async function createUserProvider(userId: string, newForm: UserSmtpIntegrationForm): Promise<UserSmtpIntegration> {
  try {
    const response = await axiosServices.post<UserSmtpIntegration>(`/api/user-smtp-integration/${userId}`, newForm);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error while getting user smtp integration', error);
    throw error;
  }
}

export async function updateUserProviderById(smtpId: string, newForm: UserSmtpIntegrationForm): Promise<UserSmtpIntegration> {
  try {
    const response = await axiosServices.put<UserSmtpIntegration>(`/api/user-smtp-integration/${smtpId}`, newForm);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error while getting user smtp integration', error);
    throw error;
  }
}

export async function deleteUserProvider(id: string): Promise<void> {
  try {
    await axiosServices.delete(`/api/user-smtp-integration/${id}`);
  } catch (error) {
    console.error('There was an error while deleting user smtp integration', error);
    throw error;
  }
}

export async function setUserProviderAsDefault(id: string): Promise<void> {
  try {
    await axiosServices.patch(`/api/user-smtp-integration/${id}`);
  } catch (error) {
    console.error('There was an error while setting user smtp integration as default', error);
    throw error;
  }
}

export async function searchUserList(params: Record<string, any> = {}): Promise<{
  data: User[];
  meta: PageMetaData;
}> {
  try {
    const response = await axiosServices.get('/api/user/find', { params });
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error getting users:', error);
    throw error;
  }
}

export async function checkUserEmail(email: string): Promise<boolean> {
  try {
    const response = await axiosServices.get(`/api/user/check-email/${email}`);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error checking user email:', error);
    throw error;
  }
}

export async function checkUserPhoneNumber(phone: string): Promise<boolean> {
  try {
    const response = await axiosServices.get(`/api/user/check-phone/${phone}`);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error checking user phone:', error);
    throw error;
  }
}

export async function checkUserSetup(): Promise<SetupChecklist> {
  try {
    const response = await axiosServices.get<SetupChecklist>(`/api/user/setup`);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error checking user setup:', error);
    throw error;
  }
}

export async function getSyncIntegrationsList(userId: string): Promise<UserSyncIntegration[]> {
  try {
    const response = await axiosServices.get<UserSyncIntegration[]>(`/api/user/list-sync-integrations/${userId}`);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error while getting user smtp integration', error);
    throw error;
  }
}

export async function getOutboundEmailOptions(userId: string): Promise<UserSyncIntegration[]> {
  try {
    const response = await axiosServices.get<UserSyncIntegration[]>(`/api/user/list-outbound-email-options/${userId}`);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error while getting user smtp integration', error);
    throw error;
  }
}

export async function updateUserDefaultOutboundEmailProvider(userId: string, emailName: string): Promise<UserSyncIntegration[]> {
  try {
    const response = await axiosServices.put(`/api/user/update-outbound-email/${userId}`, { emailName });
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error while getting user smtp integration', error);
    throw error;
  }
}

export async function getCalendarIntegrationOptions(userId: string): Promise<UserSyncIntegration[]> {
  try {
    const response = await axiosServices.get<UserSyncIntegration[]>(`/api/user/list-calendar-options/${userId}`);
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error while getting user smtp integration', error);
    throw error;
  }
}

export async function updateUserDefaultCalendarProvider(userId: string, calendarName: string | null): Promise<UserSyncIntegration[]> {
  try {
    const response = await axiosServices.put(`/api/user/update-calendar-provider/${userId}`, { calendarName });
    const data = response.data;
    return data;
  } catch (error) {
    console.error('There was an error while getting user smtp integration', error);
    throw error;
  }
}

export async function updateCombinedSetupStatus(userId: string, payload: Record<string, boolean>) {
  try {
    const response = await axiosServices.patch(`/api/user/${userId}/setup-status/combined`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating combined setup status:', error);
    throw error;
  }
}
